import {Inject, Injectable} from '@angular/core';
import {AuthorizationService} from '../authorization/authorization.service';
import {BehaviorSubject, Subject, Subscription, timer} from 'rxjs';
import {delay, filter, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {RxStompService} from '@stomp/ng2-stompjs';
import {EndUserLogModel} from '../end-user/log/end-user-log.model';
import {AlarmLogModel} from '../dashboard/monitoring/alarm-log.model';
import {AlarmType} from '../unit-log-manager/alarm-type';
// import {UnitLogManagerService} from '../unit-log-manager/unit-log-manager.service';

@Injectable({
  providedIn: 'root'
})
export class WebStompService {
  protected subscriptions: Array<Subscription> = [];
  protected lastPing: BehaviorSubject<number> = new BehaviorSubject<number>(Math.floor((new Date()).getTime() / 1000));
  protected queueName: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private readonly pingCheckIntervalMs = 20000;
  private readonly pingAgeAlarmSec = 10;
  protected modeChangeSubscription: Subscription;
  public newUnitMessage: Subject<any> = new Subject<any>();
  public newAlarm: Subject<AlarmLogModel> = new Subject<AlarmLogModel>();
  public retranslatorChange: Subject<any> = new Subject<any>();
  public alarmClosed: Subject<{type: AlarmType, id: number}> = new Subject<{type: AlarmType, id: number}>();
  public isOnline: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected connectCount = 0;
  protected readonly maxConnectCount = 10;
  public messagesReceived: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public alarmsReceived: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public newEndUserConfirmNotification: Subject<EndUserLogModel> = new Subject<EndUserLogModel>();

  constructor(
    protected authorizationService: AuthorizationService,
    protected http: HttpClient,
    protected rxStompService: RxStompService,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint
  ) {

    this.recreateBrowserQueue();
    timer(this.pingCheckIntervalMs, this.pingCheckIntervalMs)
      .pipe(
        withLatestFrom(this.lastPing, this.isOnline)
      )
      .subscribe(
        ([timerValue, lastPing, isOnline]) => {
          const currentTime = Math.floor((new Date()).getTime() / 1000);
          if (currentTime > (lastPing + this.pingAgeAlarmSec)) {
            if (isOnline) {
              this.isOnline.next(false);
            }
            this.recreateBrowserQueue();
          } else {
            if (!isOnline) {
              this.isOnline.next(true);
            }

          }
        }
      );

  }
  protected unsubscribeAll() {
    this.subscriptions.forEach(
      subscription => subscription.unsubscribe()
    );
    this.subscriptions = [];
  }
  protected recreateBrowserQueue() {
    // if (location.origin === 'http://localhost:4200') {
    //   return;
    // }
    // if (this.browserQueueNameSubscription) {
    //   this.browserQueueNameSubscription.unsubscribe();
    //   this.browserQueueNameSubscription = null;
    // }
    this.unsubscribeAll();
    if (this.connectCount > this.maxConnectCount) {
      return;
    }
    this.subscriptions.push(
      this.authorizationService.getIdentityObservable().pipe(
        filter(
          userIdentity => !!userIdentity
        ),
        switchMap(
          () => this.http.post(
            this.environmentApiBasepoint + 'browser-queue',
            {withUnitMessages: false},
            {observe: 'body'}
          )
        ),
        map(
          (response: any) => response.id
        ),
        filter(queueName => !!queueName),
        tap(queueName => this.queueName.next(queueName)),
      ).subscribe(
        browserQueueName => {
          // this.lastPing.next(Math.floor((new Date()).getTime() / 1000));
          this.connectCount++;
          this.subscriptions.push(
            this.rxStompService.watch('/amq/queue/' + browserQueueName)
              .pipe(
                withLatestFrom(this.isEnabled)
              )
              .subscribe(([message, isEnabled]) => {
              try {
                const data = JSON.parse(message.body);
                this.lastPing.next(Math.floor((new Date()).getTime() / 1000));
                if (isEnabled) {
                  if (data.type === 'ping') {
                    this.messagesReceived.next(this.messagesReceived.getValue() + 1);
                  }
                  if (data.type === 'unitMessage') {
                    this.newUnitMessage.next(data.message);
                    this.messagesReceived.next(this.messagesReceived.getValue() + 1);
                  }
                  if (data.type === 'unitAlarm') {
                    this.newAlarm.next({
                      type: 'unit',
                      id: data.message.id,
                      notification_id: data.message.notification_id,
                      notification_name: data.message.notification_name,
                      item_id: data.message.unit_id,
                      item_name: data.message.unit_name,
                      description: data.message.description,
                      timestamp: data.message.timestamp,
                      time: data.message.time,
                      retranslator_setting_id: data.message.retranslator_setting_id,
                    });
                    this.alarmsReceived.next(this.alarmsReceived.getValue() + 1);
                  }
                  if (data.type === 'endUserAlarm') {
                    this.newAlarm.next({
                      type: 'end-user',
                      id: data.message.id,
                      notification_id: data.message.end_user_notification_id,
                      notification_name: data.message.end_user_notification_name,
                      item_id: data.message.end_user_id,
                      item_name: data.message.end_user_login,
                      description: data.message.description,
                      timestamp: data.message.timestamp,
                      time: data.message.time,
                    });
                    this.alarmsReceived.next(this.alarmsReceived.getValue() + 1);
                  }
                  if (data.type === 'retranslatorChange') {
                    this.retranslatorChange.next(data.message);
                  }
                  if (data.type === 'unitAlarmClosed') {
                    this.alarmClosed.next({
                      type: 'unit',
                      id: data.message.id
                    });
                  }
                  if (data.type === 'endUserAlarmClosed') {
                    this.alarmClosed.next({
                      type: 'end-user',
                      id: data.message.id
                    });
                  }
                  if (data.type === 'confirmEndUser') {
                    this.newEndUserConfirmNotification.next(data.message);
                  }
                }
              } catch (e) { }
            })
          );
          this.subscriptions.push(
            this.rxStompService.webSocketErrors$.pipe(
              tap(val => console.log({error: val})),
              delay(1000),
            ).subscribe(
              () => this.recreateBrowserQueue()
            )
          );
          this.subscriptions.push(
            this.rxStompService.stompErrors$.pipe(
              tap(val => console.log({error: val})),
              delay(1000),
            ).subscribe(
              () => this.recreateBrowserQueue()
            )
          );
          // this.subscriptions.push(
          //   this.rxStompService.connectionState$.subscribe((state) => {
          //     // state is an Enum (Integer), RxStompState[state] is the corresponding string
          //     console.log(RxStompState[state]);
          //     if (state === RxStompState.CLOSED) {
          //       timer(5000).subscribe(() => this.recreateBrowserQueue());
          //     }
          //   })
          // );
        }
      )
    );
  }

  public setModeWithUnitMessages() {
    if (this.modeChangeSubscription) {
      this.modeChangeSubscription.unsubscribe();
      this.modeChangeSubscription = null;
    }
    this.modeChangeSubscription = this.queueName.pipe(
      filter(
        name => !!name
      ),
      switchMap(
        name => this.http.patch(
          this.environmentApiBasepoint + 'browser-queue/' + name,
          {withUnitMessages: true},
          {observe: 'body'}
        )
      )
    ).subscribe();
  }
  public setModeWithoutUnitMessages() {
    if (this.modeChangeSubscription) {
      this.modeChangeSubscription.unsubscribe();
      this.modeChangeSubscription = null;
    }
    this.modeChangeSubscription = this.queueName.pipe(
      filter(
        name => !!name
      ),
      switchMap(
        name => this.http.patch(
          this.environmentApiBasepoint + 'browser-queue/' + name,
          {withUnitMessages: false},
          {observe: 'body'}
        )
      )
    ).subscribe(
      result => {
        this.modeChangeSubscription.unsubscribe();
        this.modeChangeSubscription = null;
      }
    );
  }
}
